import React from 'react';
import ArrowRightCircleIcon from '@atlaskit/icon/glyph/arrow-right-circle';
import BulletListIcon from '@atlaskit/icon/glyph/bullet-list';
import CalendarIcon from '@atlaskit/icon/glyph/calendar';
import CheckboxIcon from '@atlaskit/icon/glyph/checkbox';
import ChevronDownCircleIcon from '@atlaskit/icon/glyph/chevron-down-circle';
import ChevronUpCircleIcon from '@atlaskit/icon/glyph/chevron-up-circle';
import ChildIssuesIcon from '@atlaskit/icon/glyph/child-issues';
import CommentIcon from '@atlaskit/icon/glyph/comment';
import EditorMentionIcon from '@atlaskit/icon/glyph/editor/mention';
import EditorNumberListIcon from '@atlaskit/icon/glyph/editor/number-list';
import EditorTextStyleIcon from '@atlaskit/icon/glyph/editor/text-style';
import IssueIcon from '@atlaskit/icon/glyph/issue';
import LabelIcon from '@atlaskit/icon/glyph/label';
import LikeIcon from '@atlaskit/icon/glyph/like';
import LinkIcon from '@atlaskit/icon/glyph/link';
import MenuExpandIcon from '@atlaskit/icon/glyph/menu-expand';
import OverviewIcon from '@atlaskit/icon/glyph/overview';
import PeopleGroupIcon from '@atlaskit/icon/glyph/people-group';
import RadioIcon from '@atlaskit/icon/glyph/radio';
import RecentIcon from '@atlaskit/icon/glyph/recent';
import StopwatchIcon from '@atlaskit/icon/glyph/stopwatch';
import WatchIcon from '@atlaskit/icon/glyph/watch';
import type { GlyphProps } from '@atlaskit/icon/types';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import EpicIcon from '../../assets/epic';
import SubtaskIcon from '../../assets/subtask';
import TaskIcon from '../../assets/task';
import {
	type JiraFieldType,
	ASSIGNEE_FIELDTYPE,
	CASCADING_SELECT_FIELDTYPE,
	CHECKBOX_FIELDTYPE,
	COMMENT_FIELDTYPE,
	CREATED_FIELDTYPE,
	CREATOR_FIELDTYPE,
	DATE_PICKER_FIELDTYPE,
	DATE_TIME_FIELDTYPE,
	DESCRIPTION_FIELDTYPE,
	DUEDATE_FIELDTYPE,
	ENVIRONMENT_FIELDTYPE,
	ISSUEID_FIELDTYPE,
	ISSUETYPE_FIELDTYPE,
	LABEL_FIELDTYPE,
	MULTI_SELECT_FIELDTYPE,
	MULTI_USERPICKER_FIELDTYPE,
	NUMBER_FIELDTYPE,
	PARENT_FIELDTYPE,
	PRIORITY_FIELDTYPE,
	RADIO_BUTTON_FIELDTYPE,
	REPORTER_FIELDTYPE,
	SELECT_FIELDTYPE,
	STATUS_FIELDTYPE,
	SYSTEM_LABELS_FIELDTYPE,
	TEXT_AREA_FIELDTYPE,
	TEXT_FIELDTYPE,
	TIME_ORIGINAL_ESTIMATE_FIELDTYPE,
	TIME_REMAINING_ESTIMATE_FIELDTYPE,
	TIME_SPENT_FIELDTYPE,
	UPDATED_FIELDTYPE,
	URL_FIELDTYPE,
	USERPICKER_FIELDTYPE,
	WATCHERS_FIELDTYPE,
	WORKLOG_FIELDTYPE,
	VOTES_FIELDTYPE,
	CATEGORY_FIELDTYPE,
	EPIC_FIELDTYPE,
	ISSUE_LINKS_FIELDTYPE,
} from '../../constants/fields';
import { SUBTASK, TASK } from '../../constants/hierarchy';
import messages from './messages';

const ICON_COLOR = token('color.icon.subtle', colors.N700);
const ICON_COLOR_DISABLED = token('color.icon.disabled', colors.N400);
const ICON_COLOR_SUBTLE = token('color.icon.subtle', colors.N700);
export const ICON_COLOR_INVALID = token('color.icon.danger', '#EF5C48');
const ICON_COLOR_SELECTED = token('color.text.accent.blue', colors.B400);

type IconColorProps = {
	isDisabled?: boolean;
	hasValidationError?: boolean;
	isSelected?: boolean;
	isSubtle?: boolean;
};

const getIconColor = ({ isDisabled, hasValidationError, isSelected, isSubtle }: IconColorProps) => {
	if (hasValidationError) return ICON_COLOR_INVALID;
	if (isSelected) return ICON_COLOR_SELECTED;
	if (isDisabled) return ICON_COLOR_DISABLED;
	if (isSubtle) return ICON_COLOR_SUBTLE;

	return ICON_COLOR;
};

export const getIcon = ({
	fieldType,
	...iconColorProps
}: IconColorProps & {
	fieldType?: JiraFieldType | null;
}) => {
	const props: GlyphProps = {
		label: '',
		size: 'medium',
		primaryColor: getIconColor(iconColorProps),
	};

	switch (fieldType) {
		case TEXT_AREA_FIELDTYPE:
		case DESCRIPTION_FIELDTYPE:
		case ENVIRONMENT_FIELDTYPE:
			return (
				<OverviewIcon
					{...props}
					testId="project-create-importer-upload-flow.common.utils.fields.overview-icon"
				/>
			);

		case STATUS_FIELDTYPE:
			return (
				<ArrowRightCircleIcon
					{...props}
					testId="project-create-importer-upload-flow.common.utils.fields.arrow-right-circle-icon"
				/>
			);
		case CATEGORY_FIELDTYPE:
			return (
				<MenuExpandIcon
					{...props}
					testId="project-create-importer-upload-flow.common.utils.fields.menu-expand-icon"
				/>
			);
		case EPIC_FIELDTYPE:
			return (
				<EpicIcon
					{...props}
					primaryColor={iconColorProps.isDisabled ? props.primaryColor : '#904EE2'}
					data-testId="project-create-importer-upload-flow.common.utils.fields.epic-issue-type-icon"
				/>
			);
		case TASK:
			return (
				<TaskIcon
					{...props}
					data-testId="project-create-importer-upload-flow.common.utils.fields.task-issue-type-icon"
				/>
			);
		case SUBTASK:
			return (
				<SubtaskIcon
					{...props}
					data-testId="project-create-importer-upload-flow.common.utils.fields.subtask-issue-type-icon"
				/>
			);

		case USERPICKER_FIELDTYPE:
		case CREATOR_FIELDTYPE:
		case REPORTER_FIELDTYPE:
		case ASSIGNEE_FIELDTYPE:
			return (
				<EditorMentionIcon
					{...props}
					testId="project-create-importer-upload-flow.common.utils.fields.editor-mention-icon"
				/>
			);

		case DATE_PICKER_FIELDTYPE:
		case CREATED_FIELDTYPE:
		case DUEDATE_FIELDTYPE:
		case UPDATED_FIELDTYPE:
			return (
				<CalendarIcon
					{...props}
					testId="project-create-importer-upload-flow.common.utils.fields.calendar-icon"
				/>
			);

		case VOTES_FIELDTYPE:
			return (
				<LikeIcon
					{...props}
					testId="project-create-importer-upload-flow.common.utils.fields.like-icon"
				/>
			);
		case MULTI_USERPICKER_FIELDTYPE:
			return (
				<PeopleGroupIcon
					{...props}
					testId="project-create-importer-upload-flow.common.utils.fields.people-group-icon"
				/>
			);

		case WATCHERS_FIELDTYPE:
			return (
				<WatchIcon
					{...props}
					testId="project-create-importer-upload-flow.common.utils.fields.watch-icon"
				/>
			);

		case NUMBER_FIELDTYPE:
			return (
				<EditorNumberListIcon
					{...props}
					testId="project-create-importer-upload-flow.common.utils.fields.editor-number-list-icon"
				/>
			);

		case DATE_TIME_FIELDTYPE:
			return (
				<RecentIcon
					{...props}
					testId="project-create-importer-upload-flow.common.utils.fields.recent-icon"
				/>
			);

		case LABEL_FIELDTYPE:
		case SYSTEM_LABELS_FIELDTYPE:
			return (
				<LabelIcon
					{...props}
					testId="project-create-importer-upload-flow.common.utils.fields.label-icon"
				/>
			);

		case SELECT_FIELDTYPE:
		case MULTI_SELECT_FIELDTYPE:
			return (
				<ChevronDownCircleIcon
					{...props}
					testId="project-create-importer-upload-flow.common.utils.fields.chevron-down-circle-icon"
				/>
			);

		case CASCADING_SELECT_FIELDTYPE:
			return (
				<BulletListIcon
					{...props}
					testId="project-create-importer-upload-flow.common.utils.fields.bullet-list-icon"
				/>
			);

		case CHECKBOX_FIELDTYPE:
			return (
				<CheckboxIcon
					{...props}
					testId="project-create-importer-upload-flow.common.utils.fields.checkbox-icon"
				/>
			);

		case PRIORITY_FIELDTYPE:
			return (
				<ChevronUpCircleIcon
					{...props}
					testId="project-create-importer-upload-flow.common.utils.fields.chevron-up-circle-icon"
				/>
			);

		case RADIO_BUTTON_FIELDTYPE:
			return (
				<RadioIcon
					{...props}
					testId="project-create-importer-upload-flow.common.utils.fields.radio-icon"
				/>
			);

		case COMMENT_FIELDTYPE:
			return (
				<CommentIcon
					{...props}
					testId="project-create-importer-upload-flow.common.utils.fields.comment-icon"
				/>
			);

		case URL_FIELDTYPE:
		case ISSUE_LINKS_FIELDTYPE:
			return (
				<LinkIcon
					{...props}
					testId="project-create-importer-upload-flow.common.utils.fields.link-icon"
				/>
			);

		case ISSUETYPE_FIELDTYPE:
		case ISSUEID_FIELDTYPE:
			return (
				<IssueIcon
					{...props}
					testId="project-create-importer-upload-flow.common.utils.fields.issue-icon"
				/>
			);

		case TIME_REMAINING_ESTIMATE_FIELDTYPE:
		case TIME_SPENT_FIELDTYPE:
		case TIME_ORIGINAL_ESTIMATE_FIELDTYPE:
		case WORKLOG_FIELDTYPE:
			return (
				<StopwatchIcon
					{...props}
					testId="project-create-importer-upload-flow.common.utils.fields.stopwatch-icon"
				/>
			);

		case PARENT_FIELDTYPE:
			return (
				<ChildIssuesIcon
					{...props}
					testId="project-create-importer-upload-flow.common.utils.fields.child-issues-icon"
				/>
			);

		default:
			return (
				<EditorTextStyleIcon
					{...props}
					testId="project-create-importer-upload-flow.common.utils.fields.editor-text-style-icon"
				/>
			);
	}
};

export const getDescriptorForNewFieldType = (fieldType: JiraFieldType) => {
	switch (fieldType) {
		case TEXT_FIELDTYPE:
			return messages.textField;
		case TEXT_AREA_FIELDTYPE:
			return messages.paragraph;
		case DATE_PICKER_FIELDTYPE:
			return messages.datePicker;
		case NUMBER_FIELDTYPE:
			return messages.number;
		case DATE_TIME_FIELDTYPE:
			return messages.dateTime;
		case LABEL_FIELDTYPE:
			return messages.label;
		case SELECT_FIELDTYPE:
			return messages.select;
		case CASCADING_SELECT_FIELDTYPE:
			return messages.cascadingSelect;
		case CHECKBOX_FIELDTYPE:
			return messages.checkbox;
		case USERPICKER_FIELDTYPE:
			return messages.userPicker;
		case MULTI_USERPICKER_FIELDTYPE:
			return messages.multiUserPicker;
		case MULTI_SELECT_FIELDTYPE:
			return messages.multiSelect;
		case RADIO_BUTTON_FIELDTYPE:
			return messages.radio;
		case URL_FIELDTYPE:
			return messages.url;
		default:
			return messages.textField;
	}
};

export const getDescriptionForNewFieldType = (fieldType: JiraFieldType) => {
	switch (fieldType) {
		case TEXT_FIELDTYPE:
			return messages.textFieldDescription;
		case TEXT_AREA_FIELDTYPE:
			return messages.paragraphDescription;
		case DATE_TIME_FIELDTYPE:
			return messages.dateTimeDescription;
		case MULTI_USERPICKER_FIELDTYPE:
			return messages.multiUserPickerDescription;
		case NUMBER_FIELDTYPE:
			return messages.numberDescription;
		case SELECT_FIELDTYPE:
			return messages.selectDescription;
		case MULTI_SELECT_FIELDTYPE:
			return messages.multiSelectDescription;
		default:
			return undefined;
	}
};
